import Vue from 'vue';
import api from '../common/api/cart.api.js';
import {ERROR_CODES} from '../common/constants/constants.js';
import eventBus from '../common/event-bus/event-bus.js';
import getAddressLabel from '../common/utils/get-address-label.js';

const SET_CARTS = 'SET_CARTS';
const SET_CART = 'SET_CART';
const SET_IS_CARTS_SHOWN = 'SET_IS_CARTS_SHOWN';
const SET_ON_PAGE_LOAD = 'SET_ON_PAGE_LOAD';

export const watchActiveAddress = (state, getters) => getters['cartsStore/activeAddress'];
export const activeAddressCallback = (newAddress) => {
	eventBus.$emit('on-current-address-changed', newAddress);
};

export const store = {
	namespaced: true,
	state: {
		carts: [],
		isCartsShown: false,
		onPageLoad: true
	},
	getters: {
		activeAddress: (state, getters) => getters.addresses.find(address => address.isActive),
		activeCartIndex: state => state.carts.findIndex(cart => cart.active),
		addresses: state => state.carts
			.filter(cart => cart.settlementResponse)
			.map(cart => ({...cart.settlementResponse, label: getAddressLabel(cart.settlementResponse), id: cart.timeStamp, isActive: cart.active})),
		notEmptyCarts: state => state.carts.filter(cart => cart.shoppingCartDTO).filter(cart => cart.shoppingCartDTO.elements.length),
		addressesWithNotEmptyCart: state => state.carts
			.filter(cart => cart.shoppingCartDTO.elements.length)
			.map(cart => ({...cart.settlementResponse, label: getAddressLabel(cart.settlementResponse), id: cart.timeStamp, isActive: cart.active})),
		numberOfItemsInCarts: state => {
			const hasCart = state.carts && state.carts.length;
			if (!hasCart) return 0;

			const numberOfItemsInCarts = state.carts.filter(cart => cart.shoppingCartDTO).reduce((acc, curr) => acc + curr.shoppingCartDTO.elements.length, 0);
			return numberOfItemsInCarts;
		},
		tiedToEndpoint: state => state.carts.filter(cart => cart.shoppingCartDTO).some(cart => cart.shoppingCartDTO.tiedToEndpoint),
		cartsValid: state => state.carts.every(cart => cart.isValid)
	},
	actions: {
		getCarts({commit, dispatch, state}) {
			return new Promise((resolve, reject) => {
				api.getCarts().then(response => {
					dispatch('setCarts', response.data);

					if (state.onPageLoad) {
						eventBus.$emit('on-no-address');
					}

					commit(SET_ON_PAGE_LOAD, false);
					resolve();
				}).catch(error => {
					eventBus.$emit('error', error);
					reject(error);
				});
			});
		},
		addToCart({commit, dispatch, getters, state}, id) {
			return new Promise((resolve) => {
				api.addToCart(id).then(response => {
					const cartClone = JSON.parse(JSON.stringify(state.carts[getters.activeCartIndex]));
					cartClone.shoppingCartDTO = response.data.shoppingCart;
					commit(SET_CART, {index: getters.activeCartIndex, cart: cartClone});

					const addedItem = state.carts[getters.activeCartIndex].shoppingCartDTO.elements.find(element => element.id == id);
					dispatch('newItemInCartModalStore/setAddedElement', addedItem, {root: true});
					eventBus.$emit('open-newItemInCartPopup');

					resolve();
				}).catch(error => {
					const err = error.response.data;

					if (err.errorCode == ERROR_CODES.CONFLICT) {
						dispatch('warningModalStore/setAddedItem', err.addedItem, {root: true});
						dispatch('warningModalStore/setInCartItems', err.inCartItems, {root: true});
						eventBus.$emit('open-conflictingItemsInCartPopup');
					} else if (err.errorCode == ERROR_CODES.SAME_SERVICE) {
						eventBus.$emit('open-sameItemInCartPopup');
					} else {
						eventBus.$emit('error', error);
					}
				});
			});
		},
		forceAddToCart({commit, dispatch, getters, state}, ids) {
			return new Promise(resolve => {
				api.forceAddToCart(ids).then(response => {
					const cartClone = JSON.parse(JSON.stringify(state.carts[getters.activeCartIndex]));
					cartClone.shoppingCartDTO = response.data.shoppingCart;
					commit(SET_CART, {index: getters.activeCartIndex, cart: cartClone});

					const addedItem = state.carts[getters.activeCartIndex].shoppingCartDTO.elements.find(element => element.id == ids.addId);
					dispatch('newItemInCartModalStore/setAddedElement', addedItem, {root: true});
					eventBus.$emit('open-newItemInCartPopup');

					resolve();
				}).catch(error => {
					eventBus.$emit('error', error);
				});
			});
		},
		removeFromCart({dispatch, state}, params) {
			return new Promise((resolve, reject) => {
				api.removeFromCart(params).then(response => {
					const modifiedCart = state.carts.find(cart => cart.timeStamp == params.cartId);
					const restCarts = state.carts.filter(cart => cart.timeStamp != params.cartId);

					const tempCart = {...modifiedCart};
					tempCart.shoppingCartDTO = response.data.shoppingCart;

					dispatch('setCarts', [...restCarts, tempCart]);
					resolve();
				}).catch(error => {
					console.error('Remove webitem from cart failed.', {error, params});
					reject();
				});
			});
		},
		resetCart({dispatch, state}, params) {
			return new Promise((resolve, reject) => {
				api.resetCart(params).then(response => {
					const modifiedCart = state.carts.find(cart => cart.timeStamp == params.cartId);
					const restCarts = state.carts.filter(cart => cart.timeStamp != params.cartId);

					const tempCart = {...modifiedCart};
					tempCart.shoppingCartDTO = response.data.shoppingCart;
					dispatch('setCarts', [...restCarts, tempCart]);
					resolve();
				}).catch(error => {
					console.error('Reset cart failed.', {error, params});
					reject(error);
				});
			});
		},
		setCarts({commit, dispatch, getters}, data) {
			commit(SET_CARTS, data);

			if (!getters.numberOfItemsInCarts) {
				dispatch('closeCarts');
			}
		},
		activateCart({dispatch, state}, params) {
			const carts = JSON.parse(JSON.stringify(state.carts));

			const currentActiveIndex = carts.findIndex(cart => cart.active);
			carts[currentActiveIndex].active = false;

			const nextActiveIndex = carts.findIndex(cart => cart.timeStamp == params.id);
			carts[nextActiveIndex].active = true;

			dispatch('setCarts', carts);
		},
		openCarts({commit}) {
			commit(SET_IS_CARTS_SHOWN, true);
		},
		closeCarts({commit}) {
			commit(SET_IS_CARTS_SHOWN, false);
		},
		setAddressAdditionalInformation({commit, state}, {cartId, additionalInformation}) {
			const index = state.carts.findIndex(cart => cart.timeStamp == cartId);
			const cartClone = JSON.parse(JSON.stringify(state.carts.find(cart => cart.timeStamp == cartId)));
			cartClone.settlementResponse = {...cartClone.settlementResponse, ...additionalInformation};

			commit(SET_CART, {
				index,
				cart: cartClone
			});
		},
		setCartAdditionalInformation({commit, state}, {cartId, additionalInformation, isValid}) {
			const index = state.carts.findIndex(cart => cart.timeStamp == cartId);
			const cartClone = JSON.parse(JSON.stringify(state.carts.find(cart => cart.timeStamp == cartId)));
			cartClone.additionalInformation = additionalInformation;
			cartClone.isValid = isValid;

			commit(SET_CART, {
				index,
				cart: cartClone
			});
		}
	},
	mutations: {
		[SET_CARTS](state, carts) {
			state.carts = carts;
		},
		[SET_ON_PAGE_LOAD](state, onPageLoad) {
			state.onPageLoad = onPageLoad;
		},
		[SET_CART](state, {index, cart}) {
			Vue.set(state.carts, index, cart);
		},
		[SET_IS_CARTS_SHOWN](state, value) {
			state.isCartsShown = value;
		}
	}
};



// WEBPACK FOOTER //
// ../source/stores/carts.store.js